"use client";

import { useTranslations } from "next-intl";
import React, { useState } from "react";
import { Button } from "./public";

interface StepperProps {
  steps: {
    label: string;
    isActive?: boolean;
    children?: React.ReactNode;
  }[];
}

const Stepper = ({ steps }: StepperProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const t = useTranslations("common");

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };
  const handlePrev = () => {
    if (activeStep !== 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  return (
    <div className="flex w-full items-center justify-center">
      <div className="s:p-8 w-full p-4">
        <div className="m-auto flex max-w-4xl items-center justify-center px-6">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`flex items-center ${
                i === steps.length - 1 ? "w-auto" : "w-full"
              }`}
            >
              {/* Dots */}
              <div className="relative flex flex-col items-center">
                <div
                  className={`h-3.5 w-3.5 rounded-full border-2 text-sm transition-colors duration-700 ${
                    i <= activeStep
                      ? "bg-primary border-primary text-white"
                      : i === 0
                        ? "bg-primary text-white"
                        : "text-ring border-ring bg-background"
                  } `}
                  aria-current={i === activeStep}
                ></div>

                {/* Labels */}
                <span
                  className={`text-md m absolute hidden font-medium sm:mt-5 sm:block ${
                    i <= activeStep ? "text-primary" : "text-ring"
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {/* Lines */}
              {i < steps.length - 1 && (
                <div className="relative mx-2 w-full flex-1">
                  <div className="bg-ring h-0.5"></div>
                  <div
                    className={`absolute ${
                      i < activeStep ? "w-full" : "w-0"
                    } bg-primary top-0 h-0.5 transition-all duration-700 ease-out`}
                  ></div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        {steps.map((step, index) => (
          <div
            key={index}
            className={`${index === activeStep ? "block" : "hidden"} s:p-4 max-xs:my-5 flex min-h-[300px] flex-col items-center justify-center max-sm:min-h-[470px] sm:my-10`}
          >
            {step.children}
          </div>
        ))}

        {/* Navigation buttons. A step that cannot be reached gets no button at
            all rather than a dead disabled one — on the final step the greyed
            out "Weiter" read as a broken control (ticket #887). The spacers
            keep "Zurück" left and "Weiter" right either way. */}
        <div
          className={`xs:flex-row xs:gap-0 mt-3 flex flex-col items-center justify-between gap-3`}
        >
          {activeStep > 0 ? (
            <Button
              className="xs:w-auto w-full"
              onClick={handlePrev}
              title={t("back")}
            />
          ) : (
            <div aria-hidden />
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              className="xs:w-auto w-full"
              onClick={handleNext}
              title={t("next")}
            />
          ) : (
            <div aria-hidden />
          )}
        </div>
      </div>
    </div>
  );
};

export default Stepper;
